import React, { useState, useEffect } from 'react';
import Breadcrumbs from '@grnhse/seedling/lib/birch/components/breadcrumbs';
import { SectionTitle } from '@grnhse/seedling/lib/birch/typography';
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@grnhse/seedling/lib/birch/components/tabs';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import { GlobalWhiteBackgroundImportant } from 'redesign_2022/global_white_background';
import { withLocalePrefix } from 'shared/utils/translation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  type UserContextValue,
  type TabKey,
  TABS,
  UserContextProvider,
} from 'talent_matching/contexts/user_context';
import {
  ALL_TABS,
  getTabIndexFromUrl,
  getTabPath,
} from 'talent_matching/lib/tab_navigation';
import { type TalentMatchingTabContextValue } from 'talent_matching/contexts/talent_matching_tab_context';
import { CompanyInfoProvider } from 'shared/components/company_info/company_info_context';
import { useDocumentTitle } from 'shared/utils/react_router_helper';
import { TalentMatchingFilters, RediscoveryProps } from 'talent_matching/types';
import TalentMatching, {
  type InitialSavedFilterSet,
} from 'talent_matching/pages/talent_matching';
import TalentRediscovery from 'talent_matching/pages/talent_rediscovery';
import TalentMatchingQueryClientProvider from 'talent_matching/talent_matching_query_client';
import styled from 'styled-components';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import {
  RankingInProgressProvider,
  type RankingTrigger,
} from 'talent_matching/contexts/ranking_in_progress_context';
import {
  CalibrationModalProvider,
  type NudgeCalloutState,
} from 'talent_matching/contexts/calibration_modal_context';
import CalibrationButton from 'talent_matching/components/calibration/calibration_button';
import CalibrationModal from 'talent_matching/components/calibration/calibration_modal';
import CalibrationNudgeModal from 'talent_matching/components/modals/calibration_nudge_modal';
import { CalibrationSuggestion } from 'talent_matching/types';
import { AdvancedFiltersProvider } from 'talent_matching/contexts/advanced_filters_context';

const t = withLocalePrefix('talent_matching');

const StyledTabs = styled(Tabs)`
  .birch-tab-list-animated-wrapper > div[role='tablist'] {
    column-gap: ${Tokens.spacing['3']};
  }
`;

const SectionTitleWrapper = styled.span`
  h3 {
    font-weight: 400;
  }
`;

type Props = {
  userContextProps: Omit<UserContextValue, 'activeTab'>;
  talentMatchingProps: Omit<
    TalentMatchingTabContextValue,
    'activeSidePanelTab' | 'setActiveSidePanelTab' | 'setSavedSearchOptions'
  > & {
    initialFilters: Partial<TalentMatchingFilters>;
    initialSavedFilterSet: InitialSavedFilterSet | null;
    previouslyCalibrated: boolean;
    rankingTrigger: RankingTrigger | null;
    pollIndexingOnLoad: boolean;
  };
  rediscoveryProps?: RediscoveryProps;
};

const TalentMatchingPageContent = ({
  userContextProps,
  talentMatchingProps,
  rediscoveryProps,
}: Props) => {
  const [activeTabIndex, setActiveTabIndex] = useState(() =>
    getTabIndexFromUrl(window.location.pathname)
  );

  const { jobTitle } = talentMatchingProps;

  const handleTabChange = (index: number) => {
    setActiveTabIndex(index);
    window.history.pushState(
      null,
      '',
      getTabPath(window.location.pathname, index)
    );
  };

  useEffect(() => {
    const onPopState = () => {
      setActiveTabIndex(getTabIndexFromUrl(window.location.pathname));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const activeTab = ALL_TABS[activeTabIndex].key;

  const { previouslyCalibrated, rankingTrigger, ...talentMatchingTabProps } =
    talentMatchingProps;

  const [showCalibrationModal, openCalibrationModal, closeCalibrationModal] =
    useModal();
  const [nudgeCalloutState, setNudgeCalloutState] =
    useState<NudgeCalloutState>(null);
  const [showNudgeReviewModal, openNudgeReviewModal, closeNudgeReviewModal] =
    useModal();
  const [pendingNudgeSuggestions, setPendingNudgeSuggestions] = useState<
    CalibrationSuggestion[] | null
  >(null);

  const onReviewModalApply = (selected: CalibrationSuggestion[]) => {
    if (selected.length > 0) {
      setPendingNudgeSuggestions(selected);
    }
    setNudgeCalloutState('applied');
  };

  const renderTabPanelContent = (key: TabKey) => {
    switch (key) {
      case TABS.APP_REVIEW:
        return <TalentMatching {...talentMatchingTabProps} />;
      case TABS.REDISCOVERY:
        if (!rediscoveryProps) {
          return null;
        }
        return <TalentRediscovery {...rediscoveryProps} />;
    }
  };

  return (
    <AdvancedFiltersProvider
      value={talentMatchingTabProps.advancedFilterConfig}
    >
      <UserContextProvider value={{ ...userContextProps, activeTab }}>
        <CompanyInfoProvider
          value={{
            canViewCompanyInfo: userContextProps.canViewCompanyInfo,
            companyProfilesPath: userContextProps.paths.companyProfilesPath,
          }}
        >
          <RankingInProgressProvider
            rankingTrigger={rankingTrigger}
            previouslyCalibrated={previouslyCalibrated}
            rankingInProgressPath={
              talentMatchingProps.paths.rankingInProgressPath
            }
          >
            <CalibrationModalProvider
              value={{
                openCalibrationModal,
                closeCalibrationModal,
                showCalibrationModal,
                paths: talentMatchingTabProps.paths,
                jobTitle: jobTitle ?? '',
                canCreateCalibrationSetting:
                  talentMatchingTabProps.canCreateCalibrationSetting,
                canEditAndDeleteTalentMatchingCalibration:
                  talentMatchingTabProps.canEditAndDeleteTalentMatchingCalibration,
                nudgeCalloutState,
                setNudgeCalloutState,
                openNudgeReviewModal: () => {
                  closeCalibrationModal();
                  openNudgeReviewModal();
                },
                pendingNudgeSuggestions,
                setPendingNudgeSuggestions,
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ marginTop: 1 }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <SectionTitle large>{t('header')}</SectionTitle>
                    <SectionTitleWrapper>
                      <SectionTitle large>{jobTitle}</SectionTitle>
                    </SectionTitleWrapper>
                  </Stack>
                  <CalibrationButton
                    rankingDisabledReason={
                      talentMatchingTabProps.rankingDisabledReason
                    }
                  />
                </Stack>
                <StyledTabs
                  index={activeTabIndex}
                  onChange={handleTabChange}
                  data-provides="talent-matching-tabs"
                >
                  <TabList>
                    {ALL_TABS.map((tab) => (
                      <Tab
                        key={tab.key}
                        data-provides={`talent-matching-${tab.key}-tab`}
                      >
                        {t(tab.label)}
                      </Tab>
                    ))}
                  </TabList>
                  <Divider />
                  <TabPanels>
                    {ALL_TABS.map((tab) => (
                      <TabPanel key={tab.key}>
                        {renderTabPanelContent(tab.key)}
                      </TabPanel>
                    ))}
                  </TabPanels>
                </StyledTabs>
              </Stack>
              <CalibrationModal
                isOpen={showCalibrationModal}
                onClose={() => {
                  closeCalibrationModal();
                  setNudgeCalloutState(null);
                }}
              />
              <CalibrationNudgeModal
                isOpen={showNudgeReviewModal}
                onClose={closeNudgeReviewModal}
                canCreateCalibrationSetting={
                  talentMatchingTabProps.canCreateCalibrationSetting
                }
                openCalibrationModal={openCalibrationModal}
                onApplySuggestions={onReviewModalApply}
                variant="review"
              />
            </CalibrationModalProvider>
          </RankingInProgressProvider>
        </CompanyInfoProvider>
      </UserContextProvider>
    </AdvancedFiltersProvider>
  );
};

const TalentMatchingPage = (props: Props) => {
  useDocumentTitle(t('document_title'));
  const { paths, jobTitle } = props.talentMatchingProps;

  return (
    <>
      <GlobalWhiteBackgroundImportant />
      <TalentMatchingQueryClientProvider>
        <Stack spacing={1}>
          <Breadcrumbs
            current={t('title')}
            crumbs={[
              {
                label: t('breadcrumbs.all_jobs'),
                href: paths.allJobsPath,
              },
              {
                label: jobTitle,
                href: paths.jobDashboardPath,
              },
            ]}
          />
          <TalentMatchingPageContent {...props} />
        </Stack>
      </TalentMatchingQueryClientProvider>
    </>
  );
};

export default TalentMatchingPage;
